import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateChangeEmailToken } from '../../../api/productService';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { useToast } from '../../../hooks/useToast';
import ConfirmationDialog from '../../../funnel/ConfirmationDialog/ConfirmationDialog';
import './ChangeEmail.css';

const ChangeEmail: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const {
    isOpen,
    config,
    primaryAction,
    secondaryAction,
    hideConfirmation,
    showEmailVerification
  } = useConfirmationDialog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email format
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      showError(t('product.changeEmail.error'));
      return;
    }

    setLoading(true);

    try {
      await generateChangeEmailToken(email);

      // Show success toast
      showSuccess(t('product.changeEmail.success') || 'Email change initiated successfully');

      // Show email verification dialog
      showEmailVerification(() => {
        navigate(-1);
      });
    } catch (error: any) {
      console.error('Error generating change email token:', error);

      // Handle structured error response
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          'Failed to initiate email change. Please try again.';

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="change-email-page">
          <div className="change-email-content">
              <h2 className="change-email-title">{t('product.changeEmail.heading')}</h2>
              <p className="change-email-desc">{t('product.changeEmail.description')}</p>

              <div className="change-email-form-group">
                  <label htmlFor="new-email" className="change-email-label">{t('product.changeEmail.newEmail')}</label>
                  <input
                      id="new-email"
                      type="email"
                      placeholder={t('product.changeEmail.placeholder')}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="change-email-input"
                      required
                      disabled={loading}
                  />
              </div>
          </div>
          <div className="change-email-footer">
              <button
                  type="submit"
                  className="change-email-submit"
                  onClick={handleSubmit}
                  disabled={!email || loading}
              >
                  {loading ? t('common.loading') : t('product.changeEmail.save')}
              </button>
          </div>
      </div>

      {config && (
        <ConfirmationDialog
          isOpen={isOpen}
          onClose={hideConfirmation}
          type={config.type || 'info'}
          title={config.title}
          message={config.message}
          primaryButtonText={config.primaryButtonText || 'Confirm'}
          secondaryButtonText={config.secondaryButtonText}
          onPrimaryAction={primaryAction || (() => {})}
          onSecondaryAction={secondaryAction || undefined}
          showIcon={config.showIcon}
        />
      )}
    </>
  );
};

export default ChangeEmail;
